import { defineStore, storeToRefs } from 'pinia'
import { ref, computed, watch } from 'vue'
import {
  ExistingCompanyLink,
  SourceCompanyId,
  ExternalCompany,
  SelectorModalExternalCompaniesParams,
} from '../types'
import { useCompanyLinksStore } from './use-company-links'
import { getExternalCompaniesSelectorModal } from '@app/application/company_links/api'
import { SfLog } from '@app/adapters/logger'

export const useExternalCompaniesStore = defineStore(
  'externalCompanies',
  () => {
    const selectedCompanies = ref<ExternalCompany[]>([])
    const defaultCompanies = ref<ExternalCompany[]>([])

    const companyLinksStore = useCompanyLinksStore()

    const { existingCompanyLinks } = storeToRefs(companyLinksStore)

    function setSelectedCompany(companyLink: ExistingCompanyLink): void {
      if (!companyLink) {
        return
      }
      const externalCompany = {
        id: companyLink.sourceCompanyId,
        name: companyLink.sourceCompanyName,
        lastSyncedAt: companyLink.lastSyncedAt,
      }
      if (
        !selectedCompanies.value.find(
          (company) => company.id === externalCompany.id
        )
      ) {
        selectedCompanies.value.push(externalCompany)
      }
    }

    function externalCompaniesFromCompanyLinks(
      companyLinks: ExistingCompanyLink[]
    ): ExternalCompany[] {
      return companyLinks.map((link) => ({
        id: link.sourceCompanyId,
        name: link.sourceCompanyName,
        lastSyncedAt: link.lastSyncedAt,
      }))
    }

    function removeSelectedCompanyById(companyId: SourceCompanyId): void {
      selectedCompanies.value = selectedCompanies.value.filter(
        (company) => company.id !== companyId
      )
    }

    function setSelectedCompanies(companyLinks: ExistingCompanyLink[]): void {
      if (!companyLinks.length) {
        return
      }
      const externalCompanies = externalCompaniesFromCompanyLinks(companyLinks)
      selectedCompanies.value = externalCompanies
    }

    function setSelectedCompaniesById(companyIds: SourceCompanyId[]): void {
      const existingCompanyLinks = useCompanyLinksStore().existingCompanyLinks
      const companyLinks = existingCompanyLinks.filter((link) => {
        return companyIds.includes(link.sourceCompanyId)
      })
      if (!companyLinks.length) {
        return
      }
      setSelectedCompanies(companyLinks)
    }

    function setDefaultCompanies(companyLinks: ExistingCompanyLink[]): void {
      if (!companyLinks.length) {
        return
      }
      const externalCompanies = externalCompaniesFromCompanyLinks(companyLinks)
      defaultCompanies.value = externalCompanies
    }

    function setDefaultCompaniesById(companyIds: SourceCompanyId[]): void {
      const existingCompanyLinks = useCompanyLinksStore().existingCompanyLinks
      const companyLinks = existingCompanyLinks.filter((link) => {
        return companyIds.includes(link.sourceCompanyId)
      })
      if (!companyLinks.length) {
        return
      }
      setDefaultCompanies(companyLinks)
    }

    function clearSelectedCompanies(): void {
      selectedCompanies.value = []
    }

    function clearDefaultCompanies(): void {
      defaultCompanies.value = []
    }

    const selectedCompany = computed(() => {
      return selectedCompanies?.value[0] || null
    })

    const hasDefaultChanged = computed(() => {
      const selectedIds = selectedCompanies.value.map((company) => company.id)
      const defaultIds = defaultCompanies.value.map((company) => company.id)

      if (defaultIds.length === 0) {
        return false
      }

      if (selectedIds.length !== defaultIds.length) {
        return true
      }

      return selectedIds.some((id) => !defaultIds.includes(id))
    })

    const restoreDefaultCompanies = (): void => {
      selectedCompanies.value = defaultCompanies.value
    }

    async function fetchSelectorExternalCompanies({
      value,
      defaultValue,
    }: SelectorModalExternalCompaniesParams): Promise<void> {
      try {
        await useCompanyLinksStore().initExistingCompanyLinks()

        const response = await getExternalCompaniesSelectorModal({
          value,
          defaultValue,
        })

        if (!response?.selectedCompaniesIds) {
          return
        }

        const { selectedCompaniesIds, defaultCompaniesIds } = response

        setSelectedCompaniesById(selectedCompaniesIds)
        if (defaultCompaniesIds) {
          setDefaultCompaniesById(defaultCompaniesIds)
        }
      } catch (error) {
        SfLog.warn(
          'Failed to fetch details for external companies selector modal',
          {
            extra: {
              error: error,
            },
          }
        )
      }
    }

    const valueToSubmit = computed(() => {
      if (defaultCompanies.value.length && !hasDefaultChanged.value) {
        return defaultCompanies.value
          .map((company) => `#${company.id}`)
          .join(',')
      } else if (selectedCompanies.value.length) {
        return selectedCompanies.value
          .map((company) => `#${company.id}`)
          .join(',')
      } else {
        return '~!'
      }
    })

    watch(
      () => existingCompanyLinks.value,
      (newValue) => {
        selectedCompanies.value = selectedCompanies.value.filter((company) =>
          newValue.some((link) => link.sourceCompanyId === company.id)
        )
      }
    )

    return {
      selectedCompanies,
      selectedCompany,
      setSelectedCompany,
      setSelectedCompaniesById,
      removeSelectedCompanyById,
      clearSelectedCompanies,
      clearDefaultCompanies,
      hasDefaultChanged,
      restoreDefaultCompanies,
      fetchSelectorExternalCompanies,
      valueToSubmit,
    }
  }
)
